import React, { useState } from "react";
import { Card, Row, Col, Steps, Button, Space, Typography, Tabs } from "antd";

const { Title, Text, Paragraph } = Typography;

// --- Network Diagram Component ---
const NetworkDiagram = ({ role }) => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "120px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Input layer */}
        <div style={{ display: "flex", marginBottom: "8px" }}>
          <div
            style={{
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              backgroundColor: "#595959",
              margin: "0 4px",
            }}
          />
        </div>

        {/* Hidden layers */}
        <div style={{ display: "flex", marginBottom: "8px" }}>
          <div
            style={{
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              backgroundColor: "#595959",
              margin: "0 4px",
            }}
          />
          <div
            style={{
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              backgroundColor: "#595959",
              margin: "0 4px",
            }}
          />
          <div
            style={{
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              backgroundColor: "#595959",
              margin: "0 4px",
            }}
          />
        </div>

        {/* Output layer */}
        <div style={{ display: "flex" }}>
          <div
            style={{
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              backgroundColor: "#595959",
              margin: "0 4px",
            }}
          />
        </div>
      </div>
    </div>
  );
};

const comparisonData = [
  {
    id: "generator",
    title: "The Generator (The Artist)",
    role: "Tries to create fakes that look real.",
    goal: "Fool the Discriminator.",
  },
  {
    id: "discriminator",
    title: "The Discriminator (The Critic)",
    role: "Tries to catch the fakes (like a detective).",
    goal: "Be right 100% of the time.",
  },
];

const linkData = [
  {
    name: "This Person Does Not Exist",
    url: "https://thispersondoesnotexist.com/",
    desc: "See hyper-realistic GAN-generated faces.",
  },
  {
    name: "Doodle to Image",
    url: "https://blogs.nvidia.com/blog/gaugan-photorealistic-landscapes-nvidia-research/",
    desc: "Read article about turning doodles into images.",
  },
];

const historyData = [
  {
    year: "2014",
    title: "The Big Bang",
    content:
      "Ian Goodfellow and his colleagues introduce the concept of GANs in a paper. The idea of two neural networks competing is instantly seen as revolutionary.",
  },
  {
    year: "2016",
    title: "DCGAN (Deep Convolutional GAN)",
    content:
      "The first major breakthrough. DCGANs stabilize the training process, allowing GANs to generate high-quality images like bedrooms and natural scenes, proving the concept works for complex visuals.",
  },
  {
    year: "2018",
    title: "StyleGAN (Nvidia)",
    content:
      "Nvidia releases StyleGAN, which can generate stunningly realistic human faces that are virtually indistinguishable from real photos. This marks the beginning of the 'fake people' era.",
  },
  {
    year: "Today",
    title: "Specialized and Faster",
    content:
      "While Diffusion Models handle broad art creation, GANs remain superior for tasks requiring extreme realism and speed, such as high-resolution image editing and specific data generation.",
  },
];

const GANExplainer = () => {
  const [trainingStep, setTrainingStep] = useState(0);

  const renderIntro = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            The Core Idea: A Two-Player Game
          </span>
        }
      >
        <Paragraph style={{ fontSize: "16px", marginBottom: "24px" }}>
          A GAN is not one AI model, but two AI systems competing
          against each other in a zero-sum game. They are constantly learning
          and improving through this adversarial{" "}
          competition.
        </Paragraph>

        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {comparisonData.map((data) => (
            <div key={data.id} style={{ marginBottom: "16px" }}>
              <Title level={5} style={{ marginBottom: "8px" }}>
                {data.title}
              </Title>
              <Paragraph style={{ marginBottom: "4px" }}>
                <Text strong>Role:</Text> {data.role}
              </Paragraph>
              <Paragraph style={{ marginBottom: 0 }}>
                <Text strong>Goal:</Text> {data.goal}
              </Paragraph>
            </div>
          ))}
        </Space>
      </Card>

      <Card
        title={<span style={{ color: "#0071e3" }}>Think of it like...</span>}
      >

        <div
          style={{
            padding: "16px",
            borderRadius: "8px",
            border: "1px solid #0071e3",
          }}
        >
          <Paragraph style={{ fontWeight: 600, marginBottom: "8px" }}>
            A Counterfeiter and Police Detective
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            The counterfeiter tries to forge money, and the detective tries to
            spot the fakes. Counterfeiter better at making convincing fakes, and
            the detective gets better at spotting subtle flaws. This goes back
            and forth until the fakes are nearly perfect.
          </Paragraph>
        </div>
      </Card>
    </Space>
  );

  const renderHistory = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {historyData.map((item, index) => (
          <Card
            key={index}
            title={
              <span style={{ color: "#0071e3" }}>
                {item.year} - {item.title}
              </span>
            }
          >
            <Paragraph style={{ marginBottom: 0 }}>{item.content}</Paragraph>
          </Card>
        ))}
      </Space>
    </Space>
  );

  const renderProcessDisplay = () => {
    const stepsContent = [
      {
        title: "Step 1: The First Pitch (G is Very Weak)",
        generatorStatus: "Output: Random Noise",
        discriminatorStatus: "Confidence: 99%",
        generatorResult: "Generator Lost",
        discriminatorResult: "Discriminator Won",
        description:
          "The Generator creates pure noise. The Discriminator easily identifies it as fake and gets a big reward for being correct.",
      },
      {
        title: "Step 2: Generator Learns (Fakes Improve)",
        generatorStatus: "Output: Blurry Image",
        discriminatorStatus: "Confidence: 70%",
        generatorResult: "Generator Lost (Still)",
        discriminatorResult: "Discriminator Won",
        description:
          "The Generator makes a blurry image. The Discriminator is less confident but still correctly rejects the fake, forcing the Generator to try harder.",
      },
      {
        title: "Step 3: Discriminator Retrains (D Learns Flaws)",
        generatorStatus: "Output: Good, but Flawed",
        discriminatorStatus: "Confidence: 90%",
        generatorResult: "Generator Lost",
        discriminatorResult: "Discriminator Won",
        description:
          "The Discriminator is trained on a new batch of real data and the best fakes, making it much better at spotting the subtle, unnatural textures.",
      },
      {
        title: "Step 4: Optimal Performance (Generator Wins!)",
        generatorStatus: "Output: Photorealistic",
        discriminatorStatus: "Confidence: 50%",
        generatorResult: "Generator Won!",
        discriminatorResult: "Discriminator Lost",
        description:
          "The competition reaches Nash Equilibrium. The Generator creates an image so realistic that the Discriminator cannot distinguish it from a real image (50% probability).",
      },
    ];

    const currentStep = stepsContent[trainingStep];
    const isGeneratorWon = trainingStep === 3;

    return (
      <Card
        title={<span style={{ color: "#0071e3" }}>{currentStep.title}</span>}
        style={{ marginTop: "24px" }}
      >
        <Row gutter={[32, 32]} justify="center" align="middle">
          <Col xs={24} md={10}>
            <Card
              title={<span style={{ color: "#0071e3" }}>Generator</span>}
              style={{
                borderColor: isGeneratorWon ? "#52c41a" : "#d9d9d9",
                borderWidth: "2px",
              }}
            >
              <NetworkDiagram role="g" />
              <Paragraph style={{ textAlign: "center", marginBottom: "4px" }}>
                <Text type="secondary">{currentStep.generatorStatus}</Text>
              </Paragraph>
              <Paragraph
                style={{
                  textAlign: "center",
                  color: isGeneratorWon ? "#52c41a" : "#000",
                  fontWeight: "600",
                  marginBottom: 0,
                }}
              >
                {currentStep.generatorResult}
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={10}>
            <Card
              title={<span style={{ color: "#0071e3" }}>Discriminator</span>}
              style={{
                borderColor: isGeneratorWon ? "#d9d9d9" : "#52c41a",
                borderWidth: "2px",
              }}
            >
              <NetworkDiagram role="d" />
              <Paragraph style={{ textAlign: "center", marginBottom: "4px" }}>
                <Text type="secondary">{currentStep.discriminatorStatus}</Text>
              </Paragraph>
              <Paragraph
                style={{
                  textAlign: "center",
                  color: isGeneratorWon ? "#000" : "#52c41a",
                  fontWeight: "600",
                  marginBottom: 0,
                }}
              >
                {currentStep.discriminatorResult}
              </Paragraph>
            </Card>
          </Col>
        </Row>
        <Paragraph
          style={{ marginTop: "24px", textAlign: "center", marginBottom: 0 }}
        >
          {currentStep.description}
        </Paragraph>
      </Card>
    );
  };

  const renderProcess = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Steps
        current={trainingStep}
        onChange={setTrainingStep}
        style={{
          "--ant-steps-icon-size": "32px",
        }}
      >
        <Steps.Step title="Initial Noise" description="G starts weak" />
        <Steps.Step title="G Tries" description="Creates blurry images" />
        <Steps.Step title="D Catches Up" description="Learns to spot flaws" />
        <Steps.Step title="G Succeeds" description="Creates perfect fakes" />
      </Steps>

      {renderProcessDisplay()}

      <Paragraph
        type="secondary"
        style={{ textAlign: "center", marginTop: "16px" }}
      >
        The visualizations above use a symbolic dots and lines structure to
        represent the neural networks of the Generator and Discriminator.
      </Paragraph>
    </Space>
  );

  const renderExamples = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Paragraph style={{ fontSize: "16px" }}>
        GANs are famous for their speed and ability to create hyper-realistic,
        yet non-existent data.
      </Paragraph>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card
            title={
              <span style={{ color: "#0071e3" }}>
                Synthetic Faces (Deepfakes)
              </span>
            }
          >
            <Paragraph style={{ marginBottom: 0 }}>
              Generating incredibly realistic photos of people who do not exist.
              Used for gaming and identity creation.
            </Paragraph>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card
            title={
              <span style={{ color: "#0071e3" }}>
                Image-to-Image Translation
              </span>
            }
          >
            <Paragraph style={{ marginBottom: 0 }}>
              Turning a sketch into a photo, or changing the style of a picture
              (e.g., turning a summer scene into a winter scene).
            </Paragraph>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card
            title={<span style={{ color: "#0071e3" }}>Data Augmentation</span>}
          >
            <Paragraph style={{ marginBottom: 0 }}>
              Creating thousands of new synthetic training examples for other AI
              models, especially in medical and security fields.
            </Paragraph>
          </Card>
        </Col>
      </Row>

      <Card title={<span style={{ color: "#0071e3" }}>Try it Yourself</span>}>
        <Paragraph style={{ fontSize: "16px", marginBottom: "16px" }}>
          Explore the technology that powers these synthetic creations:
        </Paragraph>

        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {linkData.map((link, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px",
                border: "1px solid #d9d9d9",
                borderRadius: "8px",
                backgroundColor: "white",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                  {link.name}
                </div>
                <div style={{ fontSize: "14px", color: "#666" }}>
                  {link.desc}
                </div>
              </div>
              <Button
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                type="primary"
                style={{ marginLeft: "16px" }}
              >
                Visit
              </Button>
            </div>
          ))}
        </Space>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>GANs vs. Diffusion Models</span>
        }
      >
        <Paragraph style={{ marginBottom: 0 }}>
          While GANs are fast, they can sometimes suffer from{" "}
          <Text strong>mode collapse</Text> (where the Generator finds one "good
          enough" fake and stops exploring other possibilities). Diffusion
          models generally produce higher-quality, more diverse images, but GANs
          are often faster for specific, high-resolution tasks like creating
          realistic faces.
        </Paragraph>
      </Card>
    </Space>
  );

  const tabItems = [
    {
      key: "intro",
      label: "What is a GAN?",
      children: renderIntro(),
    },
    {
      key: "history",
      label: "The Story",
      children: renderHistory(),
    },
    {
      key: "process",
      label: "Interactive Demo",
      children: renderProcess(),
    },
    {
      key: "examples",
      label: "Real Uses",
      children: renderExamples(),
    },
  ];

  return (
    <div className="page-container">
      <header
        style={{ textAlign: "center", marginBottom: "32px" }}
        className="fade-in-down"
      >
        <Title level={1}>Generative Adversarial Networks (GANs)</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          The AI Architects of Hyper-Realistic Fakes
        </Paragraph>
      </header>

      <Tabs defaultActiveKey="intro" items={tabItems} size="large" />
    </div>
  );
};

export default GANExplainer;
